/* eslint-disable jsdoc/valid-types,jsdoc/no-undefined-types */
// TODO: eslint does not know all the typescript defs (true instead of boolean, Record<>). Either use "normal" types or fix eslint?

// TODO move them to NNLIBJS

/**
 * saves the comparision on memo-ized components without params, ie. ignore if the using component supplies one
 * this does NOT inhibit rerender due to calls to any useXXX()
 *
 * @return {true}
 */
export const memoForever = () => true;

/**
 * @param {never} val
 * @param {string} [msg]
 * @returns {never}
 */
export function unreachable(val, msg = "Reached unreachable") {
	throw new Error(`${msg}: ${JSON.stringify(val)}`);
}

/**
 * @template T
 * @param {T | null | undefined} val
 * @returns {val is T}
 */
export function isDefined(val) {
	return val !== null && val !== undefined;
}

/**
 * @param {unknown} val
 * @returns {val is Record<string, unknown>}
 */
export function isRecord(val) {
	return val !== null && typeof val === 'object';
}

/**
 * @param {any} val
 * @return {string}
 */
export function nonEmptyString(val) {
	return typeof val === 'string' ? val.trim() : "";
}

/**
 * @template T
 * @template R
 * @param {(val: T) => R} f
 * @return {(a: T, b: T) => -1 | 0 | 1}
 */
export function cmpByKey(f) {
	return function cmpByKeyInner(a, b) {
		const aKey = f(a);
		const bKey = f(b);
		if (aKey === bKey) {
			return 0;
		} else if (aKey < bKey) {
			return -1;
		} else {
			return 1;
		}
	};
}

/**
 * @template T
 * @param {(a: T, b: T) => number} f
 * @return {(a: T, b: T) => -1 | 0 | 1}
 */
export function cmpInvert(f) {
	return function cmpInvertInner(a, b) {
		const res = f(a, b);
		if (res === 0) {
			return 0;
		} else if (res < 0) {
			return 1;
		} else {
			return -1;
		}
	};
}

/**
 * @param {any} feature
 * @returns {{properties: Record<string, any>}}
 */
export function cleanFeature(feature) {
	if (typeof feature !== 'object' || feature === null) {
		return {
			properties: {},
		};
	}

	const props = feature.properties;
	if (typeof props !== 'object' || props === null) {
		return {
			...feature,
			properties: {},
		};
	}

	return feature;
}

/**
 * @param {{
 * button?: number,
 * altKey: boolean,
 * ctrlKey: boolean,
 * metaKey: boolean,
 * shiftKey: boolean,
 * }} event
 * @returns {boolean}
 * */
export function isMainEvent(event) {
	return !event.altKey
		&& !event.ctrlKey
		&& !event.metaKey
		&& !event.shiftKey
		&& (event.button === undefined || event.button === 0);
}

/**
 * @param {number} millis
 * @returns {Promise<void>}
 * */
export function delay(millis) {
	if (process.env.NODE_ENV !== "production") {
		if (millis < 0) {
			throw new Error(`delay: duration (\`${millis}\`) is negative`);
		}
	}

	return new Promise((onOk) => {
		setTimeout(onOk, millis);
	});
}

/** @typedef {{
 *   initDurationMs?: number,
 *   factor?: number,
 *   maxTries?: number,
 * }} RetryExponentialOptions */

/**
 * @template R
 * @param {() => PromiseLike<R>} f
 * @param {RetryExponentialOptions} options
 * @returns {Promise<R>}
 * */
export async function retryExponential(
	f,
	{
		initDurationMs = 500,
		factor = 1.5,
		maxTries = 5,
	} = {},
) {
	let tryNum = 1;
	while (true) {
		try {
			return await f();
		} catch (err) {
			if (maxTries <= tryNum) {
				throw err;
			}
		}

		await delay(initDurationMs * Math.pow(tryNum, factor));
		tryNum += 1;
	}
}
